import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../lib/utils"

const inputVariants = cva(
  "flex w-full bg-white text-sm text-gray-900 placeholder:text-gray-400 transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:cursor-not-allowed disabled:opacity-50 outline-none",
  {
    variants: {
      variant: {
        default:
          "border border-black rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-primary/20 focus:border-primary",
        outline:
          "border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary",
        ghost:
          "border-none bg-transparent px-0 py-2 focus:ring-0",
      },
      inputSize: {
        default: "h-10",
        sm: "h-8 text-xs px-2",
        lg: "h-12 text-base px-4",
      },
    },
    defaultVariants: {
      variant: "default",
      inputSize: "default",
    },
  }
)

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">,
    VariantProps<typeof inputVariants> {
  startIcon?: React.ReactNode
  endIcon?: React.ReactNode
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, variant, inputSize, type, startIcon, endIcon, ...props }, ref) => {
    if (startIcon || endIcon) {
      return (
        <div
          className={cn(
            inputVariants({ variant, inputSize }),
            "flex items-center gap-2",
            className
          )}
        >
          {startIcon && (
            <span className="flex items-center shrink-0">{startIcon}</span>
          )}
          <input
            type={type}
            className="flex-1 bg-transparent outline-none text-sm text-gray-900 placeholder:text-gray-400 disabled:cursor-not-allowed disabled:opacity-50"
            ref={ref}
            {...props}
          />
          {endIcon && (
            <span className="flex items-center shrink-0">{endIcon}</span>
          )}
        </div>
      )
    }

    return (
      <input
        type={type}
        className={cn(inputVariants({ variant, inputSize, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Input.displayName = "Input"

export { Input, inputVariants }
